import { useTenantMemberships } from "@/contexts/TenantContext";
import { LazoPaths } from "@/enums/LazoPaths";
import useWebSocketSubscription from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/hooks/useWebSocketSubscription";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { useChangeTenantIfNeeded } from "@/hooks/useChangeTenantIfNeeded";
import { useResponsive } from "@/hooks/useResponsive";
import { LazoChatConversationShell, mapToShell } from "@/models/ai-chat/LazoChatConversation";
import { LazoChatMessage } from "@/models/ai-chat/LazoChatMessage";
import { LazoChatTypingDTO } from "@/models/ai-chat/LazoChatTyping";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import LazoChatMessageService from "@/services/ai-chat/LazoChatMessageService";
import { ConversationType } from "@/types/ConversationType";
import ChatUserWebSocketClient from "@/utils/websocket/ChatUserWebSocketClient";
import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MentionItem } from "react-mentions";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChatList } from "./ChatList";
import ChatView from "./ChatView";
import { EmptyChatsScreen } from "./empty-chat-components/EmptyChatsScreen";
import { useOneChatContext } from "./one/OneChatContext";
import { resolveDeeplinkConversation } from "./resolveDeeplinkConversation";

export type SendMessageFn = (message: string, conversation: LazoChatConversationShell, mentions?: { id: string }[], files?: File[]) => void;

export const ChatParams = {
  LazoChatTeamExternalId: ":LazoChatTeamExternalId",
};

export function ChatContainer() {
  const { t } = useTranslation("chat");
  const {
    chats,
    setChats,
    selectedChat,
    messages,
    setMessages,
    members,
    handleChatSelect,
    setNewConversationMembers,
    startNewChat,
    showMobileChatView,
    readOnly,
  } = useOneChatContext();

  useChangeTenantIfNeeded();

  const { isMobile, isTablet } = useResponsive();

  const isMobileOrTablet = isMobile || isTablet;

  const [userTyping, setUserTyping] = useState<LazoChatTypingDTO | null>();
  const [typingTimeout, setTypingTimeout] = useState<any>(null);
  const currentUserExternalId: string = AuthService.getUser()?.externalId ?? "";

  const { LazoChatTeamExternalId } = useParams();
  const [searchParams] = useSearchParams();
  const { currentTenantMembership, workspaceState } = useTenantMemberships();
  const navigate = useNavigate();
  const [selectedChatTeam, setSelectedChatTeam] = useState<boolean>(false);
  const deeplinkLookupRef = useRef<boolean>(false);
  const [showStartChatScreen, setShowStartChatScreen] = useState<boolean>(true);

  function handleStartChat() {
    // Read-only mode (embedded history) never starts a new conversation.
    if (readOnly) {
      return;
    }
    setShowStartChatScreen(false);
    handleChatSelect(null);
  }

  useWebSocketSubscription(ChatUserWebSocketClient, AuthService.getUser()?.externalId!, (s) => handleWsMessage(s));

  // START WEBSOCKET HANDLERS
  const handleWsMessage = (jsonMessage: string) => {
    const message = JSON.parse(jsonMessage);
    if (selectedChat.readOnly) {
      return;
    }

    switch (message.type) {
      case "NEW_MESSAGE":
        if (selectedChat?.externalId === message.content.conversationExternalId) {
          receiveMessage(message.content);
        } else {
          setChats((prev) =>
            prev.map((c) => {
              if (c.externalId === message.content.conversationExternalId) {
                return {
                  ...c,
                  unreadMessagesCount: c.unreadMessagesCount ? c.unreadMessagesCount + 1 : 1,
                  lastMessageText: message.content.message,
                };
              }
              return c;
            })
          );
        }
        break;
      case "USER_TYPING":
        setUserTyping(() => {
          return message.content;
        });

        if (typingTimeout) {
          clearTimeout(typingTimeout);
        }

        const newTypingTimeout = setTimeout(() => {
          setUserTyping(null);
        }, message.content.timeToLive - Date.now());
        setTypingTimeout(newTypingTimeout);

        break;
    }
  };

  function sendMessage(message: string, conversation: LazoChatConversationShell, mentions?: MentionItem[], files?: File[]) {
    if (conversation?.readOnly) {
      NotificationService.error(t("goToChatGraph.errors.notPartOfConversation"));
      return;
    }

    if (!conversation || !conversation.externalId) {
      startNewChat(message);
      return;
    }

    if (mentions) {
      addTaggedMembers(mentions, conversation);
    }

    const createMessagePromise = () => LazoChatMessageService.createNewMessage(message, conversation.externalId, false);

    const sendAttachmentsPromise = () => LazoChatMessageService.sendAttachments(conversation.externalId, files);

    if (mentions && mentions.length > 0) {
      handleMentions(mentions, conversation);
    }

    const promises = [];
    if (message) {
      promises.push(createMessagePromise());
    }
    if (files && files.length !== 0) {
      promises.push(sendAttachmentsPromise());
    }

    Promise.all(promises).then((responses) => {
      const existingMessages = messages || [];

      const newMessages = [...existingMessages, ...responses.map((response) => response.getContentOrThrowError()!)];

      setMessages(newMessages);
    });
  }

  function receiveMessage(newMessage: LazoChatMessage) {
    const messageExists = messages.some((msg) => msg.externalId === newMessage.externalId);
    if (messageExists) {
      return;
    }

    setMessages((prev) => prev.concat(newMessage));
    const newSelectedChat: LazoChatConversationShell = { ...selectedChat, lastMessageText: newMessage.message };
    handleChatSelect(newSelectedChat);
  }

  // END WEBSOCKET HANDLERS
  function handleMentions(mentions: MentionItem[], currentChat: LazoChatConversationShell) {
    LazoChatConversationService.addMentionsToConversationMembers(mentions, currentChat.externalId)
      // Ignore result, be optimistic
      .then();
  }

  // This has to be used only when we want to add people, not every time we send a message with a tag
  const addTaggedMembers = (mentions: MentionItem[], actualChat: LazoChatConversationShell) => {
    if (!mentions || mentions.length === 0) return;
    //check if user is not in conversation
    if (
      members.some(
        (member) => member.userExternalId === mentions[0].id && actualChat.conversationType === ConversationType.INTERNAL_CHANNEL
      )
    ) {
      return;
    }
    const userExternalId: string[] = mentions.map((mention) => mention.id);
    LazoChatConversationService.addTaggedMembers(userExternalId, actualChat?.externalId!).then((response) => {
      if (response.onError) {
        console.error(response.getErrorCode());
      }
    });
  };

  const location = useLocation();

  useEffect(() => {
    if (location.state) {
      const { conversation }: { conversation: LazoChatConversationShell } = location.state;
      handleChatSelect(conversation);
    }
  }, []);

  const urlTenantExternalId = searchParams.get("tenantExternalId");

  useEffect(() => {
    // For redirection on chat with KAM and not use the navigate to not reload
    if (selectedChatTeam || !chats?.length) {
      return;
    }

    const resolution = resolveDeeplinkConversation({
      conversationId: LazoChatTeamExternalId,
      placeholderId: ChatParams.LazoChatTeamExternalId,
      chats,
      urlTenantExternalId,
      currentTenantExternalId: currentTenantMembership?.tenant?.externalId,
      ownedTenantExternalIds: (workspaceState?.selectedWorkspace?.memberships ?? []).map((m) => m.tenant.externalId),
    });

    if (resolution.action === "wait") {
      return;
    }

    if (resolution.action === "select") {
      handleChatSelect(resolution.conversation);
      setSelectedChatTeam(true);
      return;
    }

    // resolution.action === "lookup": the conversation isn't in the loaded page (later page, or list still
    // loading for the target tenant). Verify it by id against the backend — which enforces membership — and
    // only show the access error if the backend actually denies it. Never deny based on the local list alone.
    if (deeplinkLookupRef.current) {
      return;
    }
    deeplinkLookupRef.current = true;
    setSelectedChatTeam(true);
    LazoChatConversationService.getAndConsumeMessages(LazoChatTeamExternalId!).then((r) => {
      r.onSuccess((conversation) => handleChatSelect(mapToShell(conversation))).onError(() => {
        NotificationService.error(t("errors.conversationAccessDenied"));
        navigate(LazoPaths.ONE_COMMUNICATIONS, { replace: true });
      });
    });
  }, [chats, urlTenantExternalId, currentTenantMembership?.tenant?.externalId]);

  return (
    <MainContainer>
      {isMobileOrTablet ? (
        <>
          {!showMobileChatView ? (
            <ListChatContainer
              sx={{
                width: "100%",
                height: "100%",
              }}
            >
              <ChatList userTyping={userTyping} handleStartChat={handleStartChat} />
            </ListChatContainer>
          ) : (
            <ChatViewContainer
              sx={{
                width: "100%",
                height: "100%",
              }}
            >
              {(chats.length !== 0 && selectedChat) || !showStartChatScreen ? (
                <ChatView
                  onChatSelect={handleChatSelect}
                  sendMessage={sendMessage}
                  userTyping={userTyping}
                  currentUserExternalId={currentUserExternalId}
                  setNewConversationMembers={setNewConversationMembers}
                />
              ) : (
                <EmptyChatsScreen handleStartChatFromEmpty={handleStartChat} readOnly={readOnly} />
              )}
            </ChatViewContainer>
          )}
        </>
      ) : (
        // Desktop Layout: Show both side by side
        <>
          <ListChatContainer sx={{ flex: "none", width: "33%" }}>
            <ChatList userTyping={userTyping} handleStartChat={handleStartChat} />
          </ListChatContainer>
          {(chats.length !== 0 && selectedChat) || !showStartChatScreen ? (
            <ChatViewContainer sx={{ flex: 2, width: "67%" }}>
              <ChatView
                onChatSelect={handleChatSelect}
                sendMessage={sendMessage}
                userTyping={userTyping}
                currentUserExternalId={currentUserExternalId}
                setNewConversationMembers={setNewConversationMembers}
              />
            </ChatViewContainer>
          ) : (
            <EmptyChatsScreen handleStartChatFromEmpty={handleStartChat} readOnly={readOnly} />
          )}
        </>
      )}
    </MainContainer>
  );
}

const ListChatContainer = styled(Box)(({ theme }) => ({
  zIndex: theme.zIndex.appBar,
  boxShadow: "0 0 1.306rem rgba(0, 0, 0, 0.1)",
  backgroundColor: "#fff",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
}));

const ChatViewContainer = styled(Box)({});

const MainContainer = styled(Box)({
  display: "flex",
  width: "100%",
  height: "100%",
  overflowY: "hidden",
});

export default ChatContainer;
